import { TFunction } from 'i18next';
import { Layers, Sort } from 'shared/assets/icons';
import {
  FilterGroup,
  FilterOption,
  FilterOptionType,
} from 'shared/types/filter';
import { DocumentOrganizationKeys } from 'shared/types/filter-document';
import { GroupByMode, SortingMode } from 'shared/types/sorting-mode';

export type Options = {
  t: TFunction;
  supportedGroupByModes?: GroupByMode[];
  supportedSortingModes?: SortingMode[];
  labelOverrides?: Partial<Record<GroupByMode, string>>;
};

export const createGroupingAndSorting = ({
  t,
  supportedSortingModes,
  supportedGroupByModes,
  labelOverrides,
}: Options): FilterGroup[] => {
  const sortOptions = [
    {
      label: t(
        `context-menus.filtering.task.grouping-and-sorting.sort.options.${SortingMode.Custom}`,
      ),
      value: SortingMode.Custom,
    },
    {
      label: t(
        `context-menus.filtering.task.grouping-and-sorting.sort.options.${SortingMode.DueDate}`,
      ),
      value: SortingMode.DueDate,
    },
    {
      label: t(
        `context-menus.filtering.task.grouping-and-sorting.sort.options.${SortingMode.Priority}`,
      ),
      value: SortingMode.Priority,
    },
    {
      label: t(
        `context-menus.filtering.task.grouping-and-sorting.sort.options.${SortingMode.DeadlineDeadline}`,
      ),
      value: SortingMode.DeadlineDeadline,
    },
    {
      label: t(
        `context-menus.filtering.task.grouping-and-sorting.sort.options.${SortingMode.Name}`,
      ),
      value: SortingMode.Name,
    },
    {
      label: t(
        `context-menus.filtering.task.grouping-and-sorting.sort.options.${SortingMode.CreatedAt}`,
      ),
      value: SortingMode.CreatedAt,
    },
  ];

  const groupByOptions = [
    {
      label:
        labelOverrides?.[GroupByMode.None] ??
        t(
          `context-menus.filtering.task.grouping-and-sorting.grouping.options.${GroupByMode.None}`,
        ),
      value: GroupByMode.None,
    },
    {
      label:
        labelOverrides?.[GroupByMode.Section] ??
        t(
          `context-menus.filtering.task.grouping-and-sorting.grouping.options.${GroupByMode.Section}`,
        ),
      value: GroupByMode.Section,
    },
    {
      label:
        labelOverrides?.[GroupByMode.LifeArea] ??
        t(
          `context-menus.filtering.task.grouping-and-sorting.grouping.options.${GroupByMode.LifeArea}`,
        ),
      value: GroupByMode.LifeArea,
    },
    {
      label:
        labelOverrides?.[GroupByMode.Goal] ??
        t(
          `context-menus.filtering.task.grouping-and-sorting.grouping.options.${GroupByMode.Goal}`,
        ),
      value: GroupByMode.Goal,
    },
    {
      label:
        labelOverrides?.[GroupByMode.Tag] ??
        t(
          `context-menus.filtering.task.grouping-and-sorting.grouping.options.${GroupByMode.Tag}`,
        ),
      value: GroupByMode.Tag,
    },
    {
      label:
        labelOverrides?.[GroupByMode.Deadline] ??
        t(
          `context-menus.filtering.task.grouping-and-sorting.grouping.options.${GroupByMode.Deadline}`,
        ),
      value: GroupByMode.Deadline,
    },
    {
      label:
        labelOverrides?.[GroupByMode.Priority] ??
        t(
          `context-menus.filtering.task.grouping-and-sorting.grouping.options.${GroupByMode.Priority}`,
        ),
      value: GroupByMode.Priority,
    },
    {
      label:
        labelOverrides?.[GroupByMode.DeadlineDeadline] ??
        t(
          `context-menus.filtering.task.grouping-and-sorting.grouping.options.${GroupByMode.DeadlineDeadline}`,
        ),
      value: GroupByMode.DeadlineDeadline,
    },
    {
      label:
        labelOverrides?.[GroupByMode.DayPart] ??
        t(
          `context-menus.filtering.task.grouping-and-sorting.grouping.options.${GroupByMode.DayPart}`,
        ),
      value: GroupByMode.DayPart,
    },
    {
      label:
        labelOverrides?.[GroupByMode.Type] ??
        t(
          `context-menus.filtering.task.grouping-and-sorting.grouping.options.${GroupByMode.Type}`,
        ),
      value: GroupByMode.Type,
    },
  ];

  const filteredSortOptions =
    !supportedSortingModes || supportedSortingModes.length
      ? supportedSortingModes?.length
        ? supportedSortingModes
            .map((mode) => sortOptions.find((option) => option.value === mode))
            .filter((option): option is (typeof sortOptions)[0] => !!option)
        : sortOptions
      : undefined;

  const filteredGroupByOptions =
    !supportedGroupByModes || supportedGroupByModes.length
      ? supportedGroupByModes?.length
        ? supportedGroupByModes
            .map((mode) =>
              groupByOptions.find((option) => option.value === mode),
            )
            .filter((option): option is (typeof groupByOptions)[0] => !!option)
        : groupByOptions
      : undefined;

  const filters: FilterOption[] = [];
  if (filteredSortOptions) {
    // Sorting
    filters.push({
      id: DocumentOrganizationKeys.SortingMode,
      label: t('context-menus.filtering.task.grouping-and-sorting.sort.label'),
      placeholder: t('general.labels.select'),
      icon: Sort,
      type: FilterOptionType.Select,
      selectOptions: filteredSortOptions,
    });
  }

  if (filteredGroupByOptions) {
    // Grouping
    filters.push({
      id: DocumentOrganizationKeys.GroupByMode,
      label: t(
        'context-menus.filtering.task.grouping-and-sorting.grouping.label',
      ),
      placeholder: t('general.labels.select'),
      icon: Layers,
      type: FilterOptionType.Select,
      selectOptions: filteredGroupByOptions,
    });
  }

  if (filters.length) {
    return [
      {
        id: 'groupingAndSorting',
        title: t('context-menus.filtering.task.grouping-and-sorting.title'),
        filters,
      },
    ];
  }

  return [];
};
